// /admin: an admin's page (RFC-050 "By grant", byj-1wv.62.17), laid out as
// /shares is (byj-1wv.51.13): tabs of rows, a row's actions behind its ⋯,
// each tab's one action beside its lead opening a short form over the page
// (components/FormDialog) whose person is found as it is typed
// (components/PersonField). Machines are by grant while pricing is
// undecided: an admin — whose email the relay was deployed with — grants a
// person machines with hours a month and an end, from the Machines tab or a
// grant's row (filled with it); and revokes. Under the
// grants on the same tab, each person's machine home, how long it is kept
// after their last machine stops, set there, and removing one now
// (byj-1wv.62.19). Files: what file storage costs, its settings, and each
// person's files (components/FileStorage.tsx, RFC-054 §6). Admins: who is
// one, and one made for a time (components/Admins.tsx, byj-1wv.72). The tab
// is kept per browser.
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import { adminManages, api, type Grant, type MachineHome } from "../api";
import { AdminBank, type AdminBankApi } from "../components/AdminBank";
import { Admins, endOfDay, type AdminsApi } from "../components/Admins";
import { AgentShares, type AgentSharesApi } from "../components/AgentShares";
import { DemoAccounts, type DemoAccountsApi } from "../components/DemoAccounts";
import { FileStorage, type FileStorageApi } from "../components/FileStorage";
import { useConfirm } from "../components/Confirm";
import { FormDialog } from "../components/FormDialog";
import { Page, PageHead, PageLead } from "../components/Page";
import { PersonField } from "../components/PersonField";
import { RowMenu, type MenuItem } from "../components/RowMenu";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { alike, eachPicked, SelectBox, SelectControls, useSelectMode } from "../components/Selection";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { AdminBilling, type AdminBillingApi } from "../components/AdminBilling";
import { useKeptChoice } from "../kept";
import { t, dayInput, whenDay, whenMinute, phrases } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The default hours a month a grant gives, short of the relay's own month. */
export const defaultGrantHours = 10;

/** An instant's day where the admin is, as a date input takes it: "" for none. */
export function dayOf(at?: string): string {
  const d = at ? new Date(at) : null;
  return !d || Number.isNaN(d.getTime()) ? "" : dayInput(d);
}

type Tab = "machines" | "files" | "billing" | "bank" | "agents" | "admins" | "demo";
const tabs: Tab[] = ["machines", "files", "billing", "bank", "agents", "admins", "demo"];
const TabKey = "admin.tab";

/** A grant as the dialog's form holds it. */
type GrantDraft = { email: string; hours: string; until: string };

const blankGrant = (email = ""): GrantDraft => ({ email, hours: String(defaultGrantHours), until: "" });

/** A grant's hours a month and its end, as the form holds them. */
const hoursOf = (g: Grant) => (g.limits.monthly_hours ? String(g.limits.monthly_hours) : "");
const untilOf = (g: Grant) => dayOf(g.expires_at);

/** A grant held, as the form that grants it again: granting again replaces it. */
const draftOfGrant = (g: Grant): GrantDraft => ({ email: g.email, hours: hoursOf(g), until: untilOf(g) });

/**
 * What several grants hold now, each value once: their hours a month, as
 * their rows say them, and their ends, so a change starts from what is there
 * (byj-1wv.51.13).
 */
function heldNow(gs: Grant[]): string {
  const hours = [...new Set(gs.map((g) => phrases.machineHours(g.month_cap_seconds)))];
  const ends = [...new Set(gs.map((g) => (g.expires_at ? t("admin:adminUntil", { w: whenDay(g.expires_at) }) : t("admin:adminNoEnd"))))];
  return t("recordings:heldNow", { what: t("admin:adminHeldGrants", { hours: hours.join(", "), ends: ends.join(", ") }) });
}

/**
 * What a grant gives: hours a month and an end, side by side; one grant's
 * dialog and several's alike. A field the grants picked hold differently is
 * `mixed`: empty, said so, each keeping its own until it is changed.
 */
function GrantLimits({ d, set, mixed }: {
  d: Omit<GrantDraft, "email">;
  set: (d: Omit<GrantDraft, "email">) => void;
  mixed?: { hours: boolean; until: boolean };
}) {
  // the hint under a field, apart from its label so the label stays its name
  const hint = (id: string, on?: boolean) =>
    on && (
      <span id={id} className="muted small">
        {t("recordings:eachKeeps")}
      </span>
    );
  return (
    <div className="field-pair">
      <div className="stack tight">
        <label className="field">
          {t("admin:adminHours")}
          <input
            type="number"
            min="0"
            step="1"
            value={d.hours}
            placeholder={mixed?.hours ? t("recordings:mixed") : undefined}
            aria-describedby={mixed?.hours ? "grant-hours-mixed" : undefined}
            onChange={(e) => set({ ...d, hours: e.target.value })}
          />
        </label>
        {hint("grant-hours-mixed", mixed?.hours)}
      </div>
      <div className="stack tight">
        <label className="field">
          {t("admin:adminExpires")}
          <input
            type="date"
            value={d.until}
            aria-describedby={mixed?.until ? "grant-until-mixed" : undefined}
            onChange={(e) => set({ ...d, until: e.target.value })}
          />
        </label>
        {hint("grant-until-mixed", mixed?.until)}
      </div>
    </div>
  );
}

/**
 * What the grant's dialog asks for: the person (none for several), the hours
 * and the end, each null where several are left as each holds it (a field
 * they held differently, not touched).
 */
type GrantChange = { email: string; hours: string | null; until: string | null };

/**
 * Granting machines, a short form over the page (components/FormDialog): to
 * a person — found by part of their address or their name, or an address
 * typed whole, kept for one who has not signed in yet — hours a month and an
 * end. A person picked who holds a grant fills it in, to change it. From a
 * grant's row (`of`) it changes that grant: the person is theirs, said with
 * how their month stands, nobody to find; Save, as granting again replaces it.
 * For several picked on their rows (`many`), the hours and the end each is
 * given again, starting from what they hold. The dialog picks; the page
 * grants.
 */
function GrantDialog({ initial, of, many, find, onSubmit, onClose }: {
  initial: GrantDraft;
  /** the grant whose row it was opened from */
  of?: Grant;
  /** the grants picked on their rows, to change at once */
  many?: Grant[];
  find: typeof api.adminUsers;
  onSubmit: (c: GrantChange) => Promise<void>;
  onClose: () => void;
}) {
  // several picked start from what they hold alike; a field they differ in
  // starts empty and keeps each one's own until it is changed
  const [start] = useState(() =>
    many ? { ...initial, hours: alike(many, hoursOf) ?? "", until: alike(many, untilOf) ?? "" } : initial,
  );
  const [d, setD] = useState(start);
  const [touched, setTouched] = useState({ hours: false, until: false });
  const mixed = many && { hours: alike(many, hoursOf) === null && !touched.hours, until: alike(many, untilOf) === null && !touched.until };
  const changed = !(of || many) || d.hours !== start.hours || d.until !== start.until || touched.hours || touched.until;
  const submit = () => onSubmit({ email: d.email, hours: mixed?.hours ? null : d.hours, until: mixed?.until ? null : d.until });
  return (
    <FormDialog
      id="grant-dialog"
      testid={many ? "grant-many" : "grant-form"}
      title={many ? t("admin:adminChangeGrantsTitle", { count: many.length }) : of ? t("admin:adminChangeGrantFor", { email: of.email }) : t("admin:adminGrantTitle")}
      now={
        of
          ? [
              t("admin:adminUsed", { used: phrases.machineHours(of.month_seconds), cap: phrases.machineHours(of.month_cap_seconds) }),
              of.expires_at ? t("admin:adminUntil", { w: whenMinute(of.expires_at) }) : t("admin:adminNoEnd"),
            ].join(" · ")
          : many && heldNow(many)
      }
      submitLabel={of || many ? t("common:save") : t("admin:adminGrant")}
      canSubmit={(many !== undefined || isAddress(d.email)) && changed}
      onSubmit={submit}
      onClose={onClose}
    >
      {!of && !many && (
        <PersonField
          find={find}
          label={t("admin:adminEmail")}
          required
          value={d.email}
          onChange={(email) => setD({ ...d, email })}
          onPick={(u) => setD(u.machines ? draftOfGrant({ ...u.machines, email: u.email }) : { ...d, email: u.email })}
          hint={t("admin:adminFindHintGrant")}
        />
      )}
      <GrantLimits
        d={d}
        mixed={mixed}
        set={(x) => {
          setTouched({ hours: touched.hours || x.hours !== d.hours, until: touched.until || x.until !== d.until });
          setD({ ...d, ...x });
        }}
      />
    </FormDialog>
  );
}

function GrantRow({ g, select, onChange, onRevoke }: {
  g: Grant;
  /** while several are being picked */
  select?: { picked: Set<string>; toggle: (id: string) => void };
  onChange: (g: Grant) => void;
  onRevoke: (g: Grant) => void;
}) {
  const sub = [
    g.name,
    t("admin:adminUsed", { used: phrases.machineHours(g.month_seconds), cap: phrases.machineHours(g.month_cap_seconds) }),
    g.expires_at ? t("admin:adminUntil", { w: whenMinute(g.expires_at) }) : t("admin:adminNoEnd"),
    g.granted_by && t("admin:adminBy", { email: g.granted_by }),
  ].filter(Boolean);
  // a grant an admin does not keep (a plan's, credits', an integration's) is shown, not changed
  const manages = adminManages(g);
  const menu: MenuItem[] = manages
    ? [
        { label: t("admin:adminChangeGrant"), onSelect: () => onChange(g) },
        { label: t("admin:adminRevoke"), onSelect: () => onRevoke(g), danger: true },
      ]
    : [];
  return (
    <li className={`share-row admin-row ${g.live ? "is-live" : "is-ended"}`} data-testid="grant">
      <span className="row-lead">{select && manages && <SelectBox id={g.user_id} title={g.email} picked={select.picked} toggle={select.toggle} />}</span>
      <span className="kind-box" aria-hidden="true">
        {initialOf(g.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{g.email}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">
        {g.source && g.source !== "admin" && (
          <span className="chip" data-testid="grant-source">
            {t(`admin:adminSource_${g.source}`)}
          </span>
        )}
        {!g.live && <span className="chip chip-warn">{t("admin:adminExpired")}</span>}
      </div>
      {menu.length > 0 && <RowMenu label={t("shares:moreFor", { title: g.email })} items={menu} />}
    </li>
  );
}

/** The days an admin picks from for a home, beside the relay's and always. */
export const homeDayChoices = [7, 14, 30, 60, 90, 180, 365];

/** How often the homes are looked at again while one is being removed. */
const homesPollMs = 5000;

/** A home's key: a person's own, or theirs for an exam (byj-1wv.62.10). */
const homeKey = (h: MachineHome) => (h.exam ? `${h.user_id}-${h.exam}` : h.user_id);

/**
 * A home's row: whose, when their last machine stopped and how long it is
 * kept; its volume's name last, set apart, for whoever looks for it on the
 * provider; its own days, when set, a chip. How long it is kept and Remove
 * now are behind ⋯; several are picked on their boxes. An exam's home
 * (byj-1wv.62.10) says its exam and that it is kept until graded, with no
 * days to set.
 */
function HomeRow({ h, busy, select, onKeep, onRemove }: {
  h: MachineHome;
  busy: boolean;
  /** while several are being picked */
  select?: { picked: Set<string>; toggle: (id: string) => void };
  onKeep: (h: MachineHome) => void;
  onRemove: (h: MachineHome) => void;
}) {
  const who = h.email || h.user_id;
  // an exam's goes once graded, or idle with nothing to grade (byj-1wv.62.20)
  const until = h.exam
    ? h.running
      ? t("admin:homeWhileRunning")
      : h.removes_at
        ? t("admin:homeRemovesOn", { w: whenDay(h.removes_at) })
        : t("admin:homeUntilGraded")
    : h.removes_at
      ? t("admin:homeRemovesOn", { w: whenDay(h.removes_at) })
      : h.running && h.retain_days !== 0
        ? t("admin:homeWhileRunning")
        : null;
  const sub = h.exam
    ? [h.name, h.last_stopped_at && t("admin:homeLastStopped", { w: whenMinute(h.last_stopped_at) }), until].filter(Boolean)
    : [h.name, h.last_stopped_at ? t("admin:homeLastStopped", { w: whenMinute(h.last_stopped_at) }) : t("admin:homeNotStopped"), until].filter(Boolean);
  // a home being removed offers neither; one whose machine runs no Remove;
  // an exam's, no days, as it is kept until graded
  const menu: MenuItem[] = busy
    ? []
    : [
        ...(h.exam ? [] : [{ label: t("admin:homeChangeKeep"), onSelect: () => onKeep(h) }]),
        ...(h.running ? [] : [{ label: t("admin:homeRemove"), onSelect: () => onRemove(h), danger: true }]),
      ];
  return (
    <li className={`share-row admin-row home-row ${h.running ? "is-live" : "is-ended"}`} data-testid="home">
      <span className="row-lead">
        {select && !busy && <SelectBox id={homeKey(h)} title={h.exam ? `${who} ${t("admin:homeForExam", { title: h.exam_title || h.exam })}` : who} picked={select.picked} toggle={select.toggle} />}
      </span>
      <span className="kind-box" aria-hidden="true">
        {initialOf(who)}
      </span>
      <div className="row-main">
        <span className="row-title">{who}</span>
        <div className="row-sub">
          {sub.join(" · ")} · <code className="home-volume">{h.home}</code>
        </div>
      </div>
      <div className="row-meta">
        {h.exam && <span className="chip">{t("admin:homeForExam", { title: h.exam_title || h.exam })}</span>}
        {h.running && <span className="chip chip-running">{t("admin:homeRunning")}</span>}
        {busy && <span className="chip chip-warn">{t("admin:homeRemoving")}</span>}
        {/* kept as set for them alone; the default says nothing */}
        {h.retain_days !== undefined && <span className="chip">{h.retain_days === 0 ? t("admin:homeKeptAlways") : t("admin:homeKeptDays", { count: h.retain_days })}</span>}
      </div>
      <RowMenu label={t("shares:moreFor", { title: who })} items={menu} />
    </li>
  );
}

/**
 * How long a home is kept after its person's last machine stops, a short
 * form over the page (components/FormDialog): the default — as Share is
 * deployed, and following it — a number of days, or always. The default's
 * own number is not offered again beside it, which would read as the same
 * choice twice; a home already set to it keeps it. For one home, or for
 * several picked, each given the same, starting from how they are kept —
 * Mixed where they differ, each keeping its own: the dialog picks the days,
 * the page sets them.
 */
function KeepDialog({ homes, defaultDays, onSubmit, onClose }: {
  homes: MachineHome[];
  defaultDays: number;
  onSubmit: (days: number | null) => Promise<void>;
  onClose: () => void;
}) {
  const one = homes.length === 1 ? homes[0]! : null;
  // as each is kept: the default, a number of days, or always (0)
  const keyOf = (h: MachineHome) => (h.retain_days === undefined ? "default" : String(h.retain_days));
  const sayOf = (h: MachineHome) => (h.retain_days === undefined ? t("admin:homeDefaultDays", { count: defaultDays }) : h.retain_days === 0 ? t("admin:homeAlways") : t("admin:homeDays", { count: h.retain_days }));
  const same = alike(homes, keyOf);
  const start = same ?? "mixed";
  const [value, setValue] = useState(start);
  const own = homes.flatMap((h) => (h.retain_days ? [h.retain_days] : []));
  const days = [...new Set([...homeDayChoices.filter((d) => d !== defaultDays), ...own])].sort((a, b) => a - b);
  return (
    <FormDialog
      id="keep-dialog"
      testid="keep-form"
      title={one ? t("admin:homeKeepFor", { email: one.email || one.user_id }) : t("admin:homeKeepForMany", { count: homes.length })}
      now={t("recordings:heldNow", { what: [...new Set(homes.map(sayOf))].join(", ") })}
      submitLabel={t("common:save")}
      canSubmit={value !== start && value !== "mixed"}
      onSubmit={() => onSubmit(value === "default" ? null : Number(value))}
      onClose={onClose}
    >
      <label className="field">
        {t("admin:homeKeep")}
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          {same === null && <option value="mixed">{t("recordings:mixedKeeps")}</option>}
          <option value="default">{t("admin:homeDefaultDays", { count: defaultDays })}</option>
          {days.map((d) => (
            <option key={d} value={String(d)}>
              {t("admin:homeDays", { count: d })}
            </option>
          ))}
          <option value="0">{t("admin:homeAlways")}</option>
        </select>
      </label>
    </FormDialog>
  );
}

/**
 * Every person's machine home, how long each is kept, and removing one now;
 * an exam's home is kept until graded: under the grants on the Machines tab,
 * a group of its own.
 */
function Homes({
  load,
  setRetention,
  remove,
}: {
  load: typeof api.adminHomes;
  setRetention: typeof api.setHomeRetention;
  remove: typeof api.removeHome;
}) {
  const [homes, setHomes] = useState<MachineHome[] | null>(null);
  const [defaultDays, setDefaultDays] = useState(30);
  const [asked, setAsked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();

  const reload = useCallback(async () => {
    try {
      const r = await load();
      setHomes(r.homes);
      setDefaultDays(r.defaultDays);
      // a removal asked for is done once its home is gone
      setAsked((a) => new Set([...a].filter((key) => r.homes.some((h) => homeKey(h) === key))));
    } catch (err) {
      setError(errText(err));
    }
  }, [load]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const removing = homes?.some((h) => h.removing || asked.has(homeKey(h))) ?? false;
  useEffect(() => {
    if (!removing) return;
    const id = setTimeout(() => void reload(), homesPollMs);
    return () => clearTimeout(id);
  }, [removing, homes, reload]);

  // the homes whose keeping is being changed, in its dialog: one from its
  // row, a refusal said there; several picked, each changed and said back,
  // those refused left picked
  const [keeping, setKeeping] = useState<{ homes: MachineHome[]; picked?: boolean } | null>(null);
  const mode = useSelectMode();
  const keep = async ({ homes: hs, picked: many }: { homes: MachineHome[]; picked?: boolean }, days: number | null) => {
    if (many) {
      const r = await eachPicked(hs, (h) => setRetention(h.user_id, days));
      setDone(phrases.bulkDone(r.done, r.refused));
      mode.keepOnly(r.left.map(homeKey));
    } else {
      await setRetention(hs[0]!.user_id, days);
      setError(null);
      setDone(t("admin:homeKeptSet", { email: hs[0]!.email || hs[0]!.user_id }));
    }
    setKeeping(null);
    await reload();
  };

  /** Several removed now, after one question; a home whose machine runs is left. */
  const removeMany = async (hs: MachineHome[]) => {
    const go = hs.filter((h) => !h.running);
    if (go.length === 0 || !(await ask(t("admin:homeRemoveManyAsk", { count: go.length }), { action: t("admin:homeRemove"), danger: true }))) return;
    const going: string[] = [];
    const r = await eachPicked(go, async (h) => {
      await remove(h.user_id, h.exam);
      going.push(homeKey(h));
    });
    setAsked((a) => new Set([...a, ...going]));
    setDone(phrases.bulkDone(r.done, r.refused));
    mode.keepOnly(r.left.map(homeKey));
    await reload();
  };

  const removeIt = async (h: MachineHome) => {
    if (!(await ask(t("admin:homeRemoveAsk", { email: h.email || h.user_id }), { action: t("admin:homeRemove"), danger: true }))) return;
    try {
      await remove(h.user_id, h.exam);
      setAsked((a) => new Set(a).add(homeKey(h)));
      setDone(t("admin:homeRemoveAsked", { email: h.email || h.user_id }));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  };

  // a home being removed is picked no more; an exam's has no days to keep
  const pickable = (homes ?? []).filter((h) => !h.removing && !asked.has(homeKey(h)));
  const picked = pickable.filter((h) => mode.picked.has(homeKey(h)));
  const keepable = picked.filter((h) => !h.exam);
  return (
    <div className="homes-group">
      {/* a group of its own under the grants, headed as Grouped heads every list */}
      <Grouped
        id="homes"
        headed
        groups={[
          {
            title: t("admin:homesTitle"),
            items: homes ?? [],
            actions: (
              <>
                <span className="muted small">{t("admin:homesDefaultIs", { count: defaultDays })}</span>
                <SelectControls
                  ids={pickable.map(homeKey)}
                  mode={mode}
                  more={() => (
                    <>
                      {keepable.length > 0 && (
                        <button className="small" aria-haspopup="dialog" onClick={() => setKeeping({ homes: keepable, picked: true })}>
                          {t("admin:homeChangeKeeps", { n: keepable.length })}
                        </button>
                      )}
                      {picked.some((h) => !h.running) && (
                        <button className="small danger" onClick={() => void removeMany(picked)}>
                          {t("admin:homeRemoveSelected", { n: picked.filter((h) => !h.running).length })}
                        </button>
                      )}
                    </>
                  )}
                />
              </>
            ),
            note: (
              <>
                <p className="muted small panel-lead">{t("admin:homesLead")}</p>
                {error && <p className="error">{t("common:error", { m: error })}</p>}
                {done && (
                  <p className="ok-text" role="status">
                    {done}
                  </p>
                )}
              </>
            ),
            none: homes === null ? !error && <p className="muted">{t("common:loading")}</p> : <EmptyRows text={t("admin:homesNone")} />,
          },
        ]}
        row={(h) => (
          <HomeRow
            key={homeKey(h)}
            h={h}
            busy={h.removing || asked.has(homeKey(h))}
            select={mode.selecting ? { picked: mode.picked, toggle: mode.toggle } : undefined}
            onKeep={(x) => {
              setDone(null);
              setKeeping({ homes: [x] });
            }}
            onRemove={(x) => void removeIt(x)}
          />
        )}
      />
      {keeping && <KeepDialog homes={keeping.homes} defaultDays={defaultDays} onSubmit={(days) => keep(keeping, days)} onClose={() => setKeeping(null)} />}
      {confirmDialog}
    </div>
  );
}

export function Admin({
  grants = api.adminGrants,
  find = api.adminUsers,
  grant = api.grant,
  revoke = api.revoke,
  homes = api.adminHomes,
  setRetention = api.setHomeRetention,
  removeHome = api.removeHome,
  files = api,
  admins = api,
  bankGrants = api,
  demo = api,
  agentShares = api,
  billing = api,
  adminUntil,
}: {
  grants?: typeof api.adminGrants;
  find?: typeof api.adminUsers;
  grant?: typeof api.grant;
  revoke?: typeof api.revoke;
  homes?: typeof api.adminHomes;
  setRetention?: typeof api.setHomeRetention;
  removeHome?: typeof api.removeHome;
  /** the Files tab's calls */
  files?: FileStorageApi;
  /** the Admins tab's calls */
  admins?: AdminsApi;
  /** the Item bank tab's calls (RFC-056) */
  bankGrants?: AdminBankApi;
  /** the Demo accounts tab's calls */
  demo?: DemoAccountsApi;
  /** the Agent shares tab's calls (byj-1wv.49) */
  agentShares?: AgentSharesApi;
  /** the Billing tab's calls (RFC-065) */
  billing?: AdminBillingApi;
  /** for an admin by delegation, when it ends */
  adminUntil?: string;
}) {
  const [tab, setTab] = useKeptChoice<Tab>(TabKey, "machines", tabs);
  const [list, setList] = useState<Grant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // the grant's dialog: open with what it is filled with, closed as null;
  // from a grant's row (`of`) or for the grants picked (`many`)
  const [granting, setGranting] = useState<{ d: GrantDraft; of?: Grant; many?: boolean } | null>(null);
  const [counts, setCounts] = useState<Partial<Record<Tab, number>>>({});
  const [ask, confirmDialog] = useConfirm();
  // each tab's count, as the tab says it once loaded; stable, as a tab's loader depends on it
  const filesCount = useCallback((n: number) => setCounts((c) => ({ ...c, files: n })), []);
  const adminsCount = useCallback((n: number) => setCounts((c) => ({ ...c, admins: n })), []);
  const bankCount = useCallback((n: number) => setCounts((c) => ({ ...c, bank: n })), []);
  const billingCount = useCallback((n: number) => setCounts((c) => ({ ...c, billing: n })), []);
  const demoCount = useCallback((n: number) => setCounts((c) => ({ ...c, demo: n })), []);
  const agentsCount = useCallback((n: number) => setCounts((c) => ({ ...c, agents: n })), []);

  const load = useCallback(async () => {
    try {
      setList(await grants());
    } catch (err) {
      setError(errText(err));
    }
  }, [grants]);
  useEffect(() => {
    void load();
  }, [load]);

  const open = (g: { d: GrantDraft; of?: Grant; many?: boolean }) => {
    setDone(null);
    setGranting(g);
  };
  const close = () => setGranting(null);

  /** A grant to `email` of the hours and end given, from the source of the grant it changes (RFC-065 D8). */
  const grantTo = (email: string, d: { hours: string; until: string }, source?: Grant["source"]) => {
    const h = Number(d.hours);
    return grant({
      email: email.trim(),
      ...(source === "contract" ? { source } : {}),
      limits: d.hours.trim() && h > 0 ? { monthly_hours: h } : {},
      ...(endOfDay(d.until) ? { expires_at: endOfDay(d.until) } : {}),
    });
  };

  const revokeIt = async (g: Grant) => {
    if (!(await ask(t("admin:adminRevokeAsk", { email: g.email }), { action: t("admin:adminRevoke"), danger: true }))) return;
    try {
      await revoke(g.user_id, g.addon, g.source);
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };

  // several grants picked on their rows (byj-1wv.51.13): each given the same
  // hours and end, or revoked after one question; what was done said back
  const mode = useSelectMode();
  const all = list ?? [];
  // billing's and an integration's grants are listed, never picked: an admin does not change them
  const picked = all.filter((g) => adminManages(g) && mode.picked.has(g.user_id));
  // one granted, said so, a refusal said in the dialog, which stays open;
  // several, each given the change — a field left as each holds it — what
  // was done said back and those refused left picked
  const saveGrant = async (many: boolean, c: GrantChange) => {
    if (many) {
      const r = await eachPicked(picked, (g) => grantTo(g.email, { hours: c.hours ?? hoursOf(g), until: c.until ?? untilOf(g) }, g.source));
      setDone(phrases.bulkDone(r.done, r.refused));
      mode.keepOnly(r.left.map((g) => g.user_id));
    } else {
      const g = await grantTo(c.email, { hours: c.hours ?? "", until: c.until ?? "" }, granting?.of?.source);
      setDone(t("admin:adminGranted", { email: g.email }));
      setError(null);
    }
    setGranting(null);
    await load();
  };
  const revokeMany = async () => {
    if (picked.length === 0 || !(await ask(t("admin:adminRevokeManyAsk", { count: picked.length }), { action: t("admin:adminRevoke"), danger: true }))) return;
    const r = await eachPicked(picked, (g) => revoke(g.user_id, g.addon, g.source));
    setDone(phrases.bulkDone(r.done, r.refused));
    mode.keepOnly(r.left.map((g) => g.user_id));
    await load();
  };

  const grantAction = (
    // its own when it opened it — a new grant — not a grant's row, which gets the focus back itself
    <button
      className="primary"
      aria-haspopup="dialog"
      aria-expanded={granting !== null && !granting.of && !granting.many}
      aria-controls={granting && !granting.of && !granting.many ? "grant-dialog" : undefined}
      onClick={() => open({ d: blankGrant() })}
    >
      {t("admin:adminGrantTitle")}
    </button>
  );
  const grantsHead = (
    <SelectControls
      ids={all.map((g) => g.user_id)}
      mode={mode}
      more={(n) => (
        <>
          <button className="small" aria-haspopup="dialog" onClick={() => open({ d: blankGrant(), many: true })}>
            {t("admin:adminChangeGrants", { n })}
          </button>
          <button className="small danger" onClick={() => void revokeMany()}>
            {t("admin:adminRevokeSelected", { n })}
          </button>
        </>
      )}
    />
  );
  const head: Record<Tab, string> = {
    machines: t("admin:adminTabMachines"),
    files: t("admin:adminTabFiles"),
    billing: t("admin:adminTabBilling"),
    bank: t("admin:adminTabBank"),
    agents: t("admin:adminTabAgents"),
    admins: t("admin:adminTabAdmins"),
    demo: t("admin:adminTabDemo"),
  };
  const count: Partial<Record<Tab, number>> = { ...counts, ...(list ? { machines: list.length } : {}) };
  const panel = (id: Tab) => ({
    role: "tabpanel",
    id: panelId("admin", id),
    "aria-labelledby": tabId("admin", id),
    hidden: tab !== id,
    className: "rows-panel",
  });

  return (
    <Page className="admin-page">
      <PageHead title={t("admin:adminTitle")} />
      <PageLead>{t("admin:adminLead")}</PageLead>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}

      <Tabs
        prefix="admin"
        label={t("admin:adminTabs")}
        value={tab}
        onChange={setTab}
        tabs={tabs.map((id) => ({
          id,
          label: (
            <>
              {head[id]}
              {count[id] !== undefined && <span className="tab-count">{count[id]}</span>}
            </>
          ),
        }))}
      />

      <div {...panel("machines")} data-testid="grants-panel">
        {/* the tab's one way to add, beside what the tab is: apart from what acts on the rows picked */}
        <div className="panel-head">
          <p className="muted small panel-lead">{t("admin:adminMachinesLead")}</p>
          {grantAction}
        </div>
        {list === null ? (
          !error && <p className="muted">{t("common:loading")}</p>
        ) : all.length === 0 ? (
          <EmptyRows text={t("admin:adminNoGrants")} />
        ) : (
          <Grouped
            id="grants"
            // headed always: the homes' group follows on the same tab
            headed
            groups={[
              { title: t("admin:adminGrantsLive"), items: all.filter((g) => g.live), className: "rows-head-live", actions: grantsHead, none: <EmptyRows text={t("admin:adminNoneLive")} /> },
              { title: t("admin:adminGrantsEnded"), items: all.filter((g) => !g.live) },
            ]}
            row={(g) => (
              <GrantRow
                key={`${g.user_id}:${g.source ?? "admin"}`}
                g={g}
                select={mode.selecting ? { picked: mode.picked, toggle: mode.toggle } : undefined}
                onChange={(x) => open({ d: draftOfGrant(x), of: x })}
                onRevoke={(x) => void revokeIt(x)}
              />
            )}
          />
        )}
        <Homes load={homes} setRetention={setRetention} remove={removeHome} />
      </div>

      <div {...panel("files")}>
        <FileStorage client={files} onCount={filesCount} />
      </div>

      <div {...panel("billing")}>
        <AdminBilling client={billing} onCount={billingCount} />
      </div>

      <div {...panel("bank")}>
        <AdminBank client={bankGrants} onCount={bankCount} />
      </div>

      <div {...panel("agents")}>
        <AgentShares client={agentShares} onCount={agentsCount} />
      </div>

      <div {...panel("admins")}>
        <Admins client={admins} until={adminUntil} onCount={adminsCount} />
      </div>

      <div {...panel("demo")}>
        <DemoAccounts client={demo} onCount={demoCount} />
      </div>

      {granting && (
        <GrantDialog
          initial={granting.d}
          of={granting.of}
          many={granting.many ? picked : undefined}
          find={find}
          onSubmit={(c) => saveGrant(!!granting.many, c)}
          onClose={close}
        />
      )}
      {confirmDialog}
    </Page>
  );
}
